import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import {getEventStatus} from "../../../api/eventStatusAPI";
import { ConvertOIDValueToString } from "../../../utils/objectConverter";
import { useTranslation } from "react-i18next";
import {
  renderAlarmm,
  renderWarningg,
  renderNormalGray,
} from "../alarm-history/StatusChip";
import { useRefreshInterval } from "../../../contexts/RefreshIntervalContext";
import { usePageVisibility } from "../../../hooks/usePageVisibility";
import { getPlaceIdFromPathname } from "../../../utils/pathUtils";
import { useObjectData } from "../../../hooks/useLocalStorageData";

export default function ActiveAlarmsDataGrid({
  onCountsChange,
  refreshInterval,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const isPageVisible = usePageVisibility();
  const [alarms, setAlarms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { interval, isRunning } = useRefreshInterval();
  // Use custom hook to get objectData from localStorage
  const objectData = useObjectData();

  const columns = useMemo(
    () => [
      {
        field: "eventName",
        headerName: t("alarmHistory.eventName"),
        flex: 1.5,
        minWidth: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "status",
        headerName: t("alarmHistory.status"),
        flex: 1,
        minWidth: 100,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          if (params.value === "Alarm") {
            return renderAlarmm(params.value, t);
          } else if (params.value === "Warning") {
            return renderWarningg(params.value, t);
          } else if (params.value === "Normal") {
            return renderNormalGray(params.value, t);
          }
          return null;
        },
      },
      {
        field: "value",
        headerName: t("alarmHistory.value"),
        flex: 1,
        minWidth: 100,
        align: "center",
        headerAlign: "center",
      },
    ],
    [t]
  );

  const rawId = getPlaceIdFromPathname(location.pathname);
  const node_id = rawId ? parseInt(rawId, 10) : null;

  const fetchAlarms = useCallback(async () => {
    if (isLoading) return;
    if (!isRunning) {
      // When stopped, don't fetch new data but keep existing data
      return;
    }
    // Sayfa görünür değilse veri çekme
    if (!isPageVisible) return;

    const currentTime = Date.now();
    if (currentTime - lastFetchTime < 5000) return;

    setIsLoading(isInitialLoad);
    setLastFetchTime(currentTime);

    try {
      const DASHBOARD_EVENTS = [
        "Otomat Durumu",
        "POS Ünitesi",
        "Nakit Üniteleri",
        "Satış Durumu",
        "Sıcaklık",
        "Sinyal Düzeyi",
        "Stok Seviyesi",
        "Kapı Durumu",
      ];
      const allowedEventIds = new Set(
        objectData
          .filter((obj) => DASHBOARD_EVENTS.includes(obj.name))
          .map((obj) => obj.code)
      );

      const result = await getEventStatus();

      const nodeFiltered = result
        .filter((item) => item.nodeId === node_id)
        .filter((item) => allowedEventIds.has(item.event));

      // console.log("Filtered:", JSON.stringify(nodeFiltered, null, 2));

      const warnings = nodeFiltered.filter(
        (item) => item.alarmType === 1
      ).length;
      const alerts = nodeFiltered.filter((item) => item.alarmType === 2).length;

      if (onCountsChange) {
        onCountsChange({ warnings, alerts });
      }

      const mappedData = nodeFiltered.map((item) => {
        const match = objectData.find((d) => d.code === item.event);

        let status;
        if (item.alarmType === 0) {
          status = "Normal";
        } else if (item.alarmType === 1) {
          status = "Warning";
        } else if (item.alarmType === 2) {
          status = "Alarm";
        } else {
          status = "Unknown";
        }

        const date = new Date(item.epoch * 1000).toLocaleString();

        const convertedValue = ConvertOIDValueToString(
          { Type: match?.type },
          item.value
        );

        return {
          ...item,
          id: `${item.nodeId}-${item.event}-${item.epoch}`,
          eventName: match?.name || item.event,
          value: convertedValue,
          status: status,
          description: convertedValue,
          date: date,
        };
      });

      setAlarms(mappedData);
    } catch (error) {
      console.error("Error fetching alarms:", error);
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  }, [
    node_id,
    onCountsChange,
    isLoading,
    lastFetchTime,
    isRunning,
    objectData,
    isInitialLoad,
    isPageVisible,
  ]);

  // Debounced refresh handler
  const debouncedRefresh = useCallback(() => {
    if (!isLoading) {
      fetchAlarms();
    }
  }, [fetchAlarms, isLoading]);

  useEffect(() => {
    if (!isRunning) {
      // When stopped, don't fetch new data but keep existing data
      return;
    }

    const initialFetchTimeout = setTimeout(() => {
      fetchAlarms();
    }, 500);

    const intervalMs = Math.max((interval || 15) * 1000, 1000);
    const intervalId = setInterval(() => {
      debouncedRefresh();
    }, intervalMs);

    return () => {
      clearTimeout(initialFetchTimeout);
      clearInterval(intervalId);
    };
  }, [fetchAlarms, interval, debouncedRefresh, isRunning]);

  // Memoize columns to prevent unnecessary re-renders
  const memoizedColumns = useMemo(() => columns, [columns]);
  return (
    <Box
      sx={{
        height: { xs: 400, sm: 400, md: "100%" },
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <DataGrid
        rows={alarms}
        columns={memoizedColumns}
        loading={isLoading}
        hideFooter={true}
        disableRowSelectionOnClick
        density="compact"
        columnVisibilityModel={{
          description: { xs: false, sm: true },
          date: { xs: true },
        }}
        sx={{
          "& .MuiDataGrid-cell": {
            whiteSpace: "normal",
            lineHeight: "normal",
            padding: "8px",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "background.paper",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
          },
          "& .MuiDataGrid-main": {
            width: "100%",
            overflow: "auto",
          },
          "& .MuiDataGrid-virtualScroller": {
            overflow: "auto",
          },
        }}
        localeText={{ noRowsLabel: "No Alarms" }}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: "status",
                sort: "asc",
              },
            ],
          },
        }}
        sortingOrder={["asc", "desc"]}
        sortingMode="client"
        sortModel={[{ field: "status", sort: "asc" }]}
        sortComparator={(v1, v2, cellParams, mode) => {
          if (cellParams.field === "status") {
            const statusOrder = { Alarm: 0, Warning: 1, Normal: 2, Unknown: 3 };
            return statusOrder[v1] - statusOrder[v2];
          }
          return v1 > v2 ? 1 : v1 < v2 ? -1 : 0;
        }}
        getRowClassName={(params) => {
          return params.row.status === "Alarm"
            ? "alarm-row"
            : params.row.status === "Warning"
            ? "warning-row"
            : "";
        }}
      />
    </Box>
  );
}
